import * as React from 'react';

import { ScrollArea as ArkScrollArea } from '@ark-ui/react';

import { assignRef } from '../../lib/assign-ref';
import { cn } from '../../lib/utils';
import { ScrollAreaViewportContext } from './scroll-area-context';

type Scrollbars = 'x' | 'y' | 'both';
type ScrollbarVisibility = 'always' | 'hover';

interface ScrollAreaRootProps extends ArkScrollArea.RootProps {
	viewportRef?: React.Ref<HTMLDivElement>;
	scrollbars?: Scrollbars;
	scrollbarVisibility?: ScrollbarVisibility;
	scrollHideDelay?: number;
}

const ScrollAreaRoot = React.forwardRef<HTMLDivElement, ScrollAreaRootProps>(
	(
		{
			className,
			children,
			scrollbars = 'both',
			scrollbarVisibility = 'always',
			scrollHideDelay = 600,
			viewportRef,
			style,
			...props
		},
		ref
	) => {
		const [viewport, setViewport] = React.useState<HTMLDivElement | null>(null);
		const showVertical = scrollbars === 'y' || scrollbars === 'both';
		const showHorizontal = scrollbars === 'x' || scrollbars === 'both';
		const rootStyle = {
			...style,
			'--scroll-area-hide-delay': `${scrollHideDelay}ms`
		} as React.CSSProperties;
		const setViewportRef = React.useCallback(
			(node: HTMLDivElement | null) => {
				setViewport(node);
				assignRef(viewportRef, node);
			},
			[viewportRef]
		);

		return (
			<ScrollAreaViewportContext.Provider value={viewport}>
				<ArkScrollArea.Root
					ref={ref}
					className={cn('pt-scroll-area', className)}
					data-scrollbar-visibility={scrollbarVisibility}
					style={rootStyle}
					{...props}
				>
					<ArkScrollArea.Viewport
						ref={setViewportRef}
						className="pt-scroll-area-viewport"
						style={{
							overflowX: showHorizontal ? 'scroll' : 'hidden',
							overflowY: showVertical ? 'scroll' : 'hidden'
						}}
					>
						<ArkScrollArea.Content className="pt-scroll-area-content" style={{ minWidth: '0' }}>
							{children}
						</ArkScrollArea.Content>
					</ArkScrollArea.Viewport>

					{showVertical && (
						<ArkScrollArea.Scrollbar orientation="vertical" className="pt-scroll-area-scrollbar">
							<ArkScrollArea.Thumb className="pt-scroll-area-thumb" />
						</ArkScrollArea.Scrollbar>
					)}

					{showHorizontal && (
						<ArkScrollArea.Scrollbar orientation="horizontal" className="pt-scroll-area-scrollbar">
							<ArkScrollArea.Thumb className="pt-scroll-area-thumb" />
						</ArkScrollArea.Scrollbar>
					)}

					{showVertical && showHorizontal && (
						<ArkScrollArea.Corner className="pt-scroll-area-corner" />
					)}
				</ArkScrollArea.Root>
			</ScrollAreaViewportContext.Provider>
		);
	}
);

ScrollAreaRoot.displayName = 'ScrollArea.Root';

function ScrollAreaViewport({ className, ...props }: ArkScrollArea.ViewportProps) {
	return <ArkScrollArea.Viewport className={cn('pt-scroll-area-viewport', className)} {...props} />;
}

function ScrollAreaContent({ className, ...props }: ArkScrollArea.ContentProps) {
	return <ArkScrollArea.Content className={cn('pt-scroll-area-content', className)} {...props} />;
}

function ScrollAreaScrollbar({ className, ...props }: ArkScrollArea.ScrollbarProps) {
	return (
		<ArkScrollArea.Scrollbar className={cn('pt-scroll-area-scrollbar', className)} {...props} />
	);
}

function ScrollAreaThumb({ className, ...props }: ArkScrollArea.ThumbProps) {
	return <ArkScrollArea.Thumb className={cn('pt-scroll-area-thumb', className)} {...props} />;
}

function ScrollAreaCorner({ className, ...props }: ArkScrollArea.CornerProps) {
	return <ArkScrollArea.Corner className={cn('pt-scroll-area-corner', className)} {...props} />;
}

const ScrollArea = Object.assign(ScrollAreaRoot, {
	Root: ScrollAreaRoot,
	RootProvider: ArkScrollArea.RootProvider,
	Viewport: ScrollAreaViewport,
	Content: ScrollAreaContent,
	Scrollbar: ScrollAreaScrollbar,
	Thumb: ScrollAreaThumb,
	Corner: ScrollAreaCorner,
	Context: ArkScrollArea.Context
});

export { ScrollArea, type ScrollAreaRootProps };
